import request from 'sb-request'
import actions from 'redux/actions'
import merge from 'deepmerge'
import { getState, reducers } from 'redux/core'
import {
  constants, deferredPromise, fbq, links, mixpanel,
  sentry, snowplow, object, dataLayer, isNotEmpty, localStorage,
} from 'common/helpers'
import {
  modifyAbTests, modifyClosedPopups, modifySubscription,
  modifyTokens,
} from 'common/helpers/modifiers/modifyResponse'
import getAbOverrides from 'common/helpers/getters/getAbOverrides'
import { getCookieValue, getFeatureToggle } from 'helpers'

import messages from './messages'

import userCommonDataQuery from './userCommonData.graphql'

import loginQuery from './login.graphql'
import registerQuery from './register.graphql'
import facebookSigninQuery from './facebookSignin.graphql'
import googleSigninQuery from './googleSignin.graphql'
import appleSigninQuery from './appleSignin.graphql'


/**
 * Check if user with passed email already exists in system
 *
 * @param {String} value - email
 * @returns {Promise} request
 */
const checkExistByEmail = (value) => {
  const email = value.trim().toLowerCase()

  if (!email) {
    return Promise.reject()
  }

  return request.get('rest/user/exists', {
    query: {
      email,
    },
    withErrorModal: false,
  })
    .then(({ exists, gender }) => {
      console.log('check user exist by email result: ', { exists, gender })
      return exists
    })
}

const bindCouponToUser = () => {
  const {
    models: { subscription: { isNotSubscribed = true, subscriptionDate } = {} },
  } = getState()
  const { code, offerType } = getCookieValue(constants.cookieNames.giftCoupon) || {}

  if (!code) {
    return Promise.resolve()
  }

  // Marketing coupons should be bound only for new subscribers
  if (!isNotSubscribed || subscriptionDate) {
    actions.misc.cookies.removeItem(constants.cookieNames.giftCoupon)

    actions.models.modals.open(constants.modals.Dialog, {
      contentMessage: messages.dialogModal.message,
      buttonTitle: messages.dialogModal.button,
    })

    return Promise.resolve()
  }

  return request.post('rest/coupon/bind', {
    body: {
      code,
      offerType,
    },
    tokenName: constants.requestTokens.bindPromoCoupon,
  })
    .then((result) => {
      const redeem = actions.models.me.updateRedeemCoupon({
        ...result,
        code,
      })

      actions.misc.cookies.setItem(constants.cookieNames.bindedCoupon, { code, offerType })
      actions.misc.cookies.removeItem(constants.cookieNames.giftCoupon)

      return redeem
    })
}

/**
 * Identifies user via mixpanel using mixpanel id
 *
 * @param {string} mixpanelId
 * @param {string} email
 */
const handleMixpanelId = ({ mixpanelId, email }) => {
  if (mixpanelId) {
    mixpanel.identify(mixpanelId)
    mixpanel.people.set({ $email: email })
  }
  else {
    mixpanel.reset()
  }

  actions.misc.cookies.setItem('scnt_mixpanel_clientid', mixpanel.get_distinct_id(), {
    domain: '.scentbird.com',
    expires: 365,
  })
}

const syncMixpanelProps = () => {
  if (typeof window.syncMixpanelSuperPropertiesCookie === 'function') {
    window.syncMixpanelSuperPropertiesCookie()
  }
}


let isTimeoutCalled = false

const setNewrelicContext = (userId, email) => {
  if (object.get(window, 'newrelic.interaction')) {
    window.newrelic.interaction()
      .setAttribute('userId', userId)
      .setAttribute('email', email)
  }
  else if (!isTimeoutCalled) {
    setTimeout(() => {
      isTimeoutCalled = true

      setNewrelicContext(userId, email)
    }, 15000)
  }
}

const setUserContext = ({ userId, email, mixpanelId }) => {
  snowplow.setUserId(userId)
  handleMixpanelId({ mixpanelId, email })
  sentry.setUserContext({ id: userId, email, mixpanelId })
  setNewrelicContext(userId, email)

  dataLayer.track('user found', {
    user: {
      email,
      id: userId,
    },
  })
}

const trackRegisterEvents = () => {
  dataLayer.track('signup')
  fbq.track('CompleteRegistration')
  snowplow.track(constants.snowplowEvents.signup, { type: 'common' }, false)
}

/**
 * Saves new split tests values to globals, tries to re-register mixpanel properties.
 */
const updateAbTests = (abTests) => {
  if (typeof window.__updateGlobals__ === 'function') {
    window.__updateGlobals__({ 'abTests': merge(abTests, getAbOverrides()) })
  }

  // Update mixpanel properties
  if (typeof window.registerMixpanel === 'function') {
    window.registerMixpanel()
  }
}

const fetchCommonData = (awaitFor = Promise.resolve()) =>
  request.post('graphql?name=userCommonData', {
    body: {
      query: userCommonDataQuery,
    },
    modifyResult: (data) => {
      const {
        abTests,
        tokens,
        redeemCoupon,
        config: { vindiciaUrl },
      } = data

      return {
        abTests: modifyAbTests(abTests),
        tokens: modifyTokens(tokens),
        redeemCoupon,
        vindiciaUrl,
      }
    },
  })
    .then((data) => {
      awaitFor.then(() => {
        const { abTests, tokens, redeemCoupon, vindiciaUrl } = data

        updateAbTests(abTests)
        actions.models.requestTokens.set(tokens)
        actions.misc.vindiciaUrl.set(vindiciaUrl)
        actions.models.me.updateRedeemCoupon(redeemCoupon)

        syncMixpanelProps()
      })
    })

/**
 * Check Auth Errors
 * @param responseData
 * @returns {Promise<Promise<never>|undefined>}
 */
const checkErrors = async ({ responseData }) => {
  if (responseData.errors && responseData.errors.length) {
    return Promise.reject(responseData)
  }
}

/**
 * Call Auth Error Processing
 * @param title {String}
 * @param [error] {Object}
 * @param [errorCallback] {Function}
 * @param [errors] {Array}
 * @param [token] {String}
 * @param [statusCode] {Number}
 * @param [input] {Object}
 * @param [err] {Object}
 * @returns {Promise<Promise<never>>}
 */
const callErrorProcessing = async ({ title, error, errorCallback, errors, statusCode, token, input, err }) => {
  // check errors
  if (errors) {
    const errorProcessed = errors.some((errorData) => {
      const { error } = errorData

      if (error === 'user_should_reset_password') {
        const link = `${links.recoverPassword}?email=${encodeURIComponent(input.email || '')}`

        actions.models.router.push(link)

        return true
      }

      return false
    })

    if (errorProcessed) {
      return Promise.reject({ token })
    }
  }

  // TODO disable bcs this should be checked only on backend side - added on 3/26/20 by pavelivanov
  // if (errors) {
  //   errors.forEach((errorData) => {
  //     const { error, message, timestamp, additionalFields } = errorData
  //     // TODO added on 2019-08-21 by @vkudla uncomment wrong token error when https://scentbird.atlassian.net/browse/SB-238 will be ready
  //     if (![ 'no_user_or_incorrect_password', 'wrong_token', 'not_valid', 'email_exists' ].includes(error)) {
  //       sentry.captureException(`${title} failed`, {
  //         extra: {
  //           error,
  //           input,
  //           message,
  //           timestamp,
  //           additionalFields,
  //         },
  //       })
  //     }
  //   })
  // }
  // else if ((err && !err.message.toLowerCase().includes('request has been terminated')) || !err) {
  //   sentry.captureException(title, {
  //     extra: {
  //       error,
  //       input,
  //     },
  //   })
  // }

  if (typeof errorCallback === 'function') {
    errorCallback()
  }
  else {
    actions.models.modals.open(constants.modals.SomethingWentWrong, {
      endpoint: title,
      errors,
      error,
      title,
      statusCode,
    })
  }

  return Promise.reject({ token })
}

/**
 * Update Token on promise reject
 * @param errorData
 * @returns {Promise<never>}
 */
const handleRejectUpdateToken = (errorData) => {
  const { token, tokenName } = errorData

  if (token) {
    actions.models.requestTokens.update({ [tokenName]: token })
  }

  return Promise.reject(errorData)
}

/**
 * login Gql
 * @param email {String}
 * @param password {String}
 * @param token {String}
 * @param errorCallback {Function}
 * @returns {Promise<{authenticationValid: *, subscriptionProgress: *, me: *, realEmail: *, subscription: *, closedPopups: *, token: *}>}
 */
const loginGql = async ({ username: email, password, token }, errorCallback) => {
  try {
    const responseData = await request.post('graphql?name=loginGql', {
      body: {
        query: loginQuery,
        variables: { input: { email, password, token } },
      },
    })

    await checkErrors({ responseData: responseData.login })

    return getUserData(responseData.login)
  }
  catch (error) {
    const token = error.token || object.get(error, 'data.login.token')

    await callErrorProcessing({
      ...error,
      token,
      title: 'loginGql',
      errorCallback,
      input: {
        email,
        token,
      },
    })
  }
}

/**
 * We send freeTrial property on registration to backend
 * Backend passes this property to the iterable
 * This needed to send free trial offer by email instead of 25off
 *
 * @returns {boolean} Returns true if a user from free trial flow
 */
const getIsFromFreeTrialLanding = () => Boolean(getCookieValue(constants.cookieNames.isFreeTrialCoupon))

/**
 * Authorize user by email and password
 *
 * @param {object} values
 * @param {string} values.email
 * @param {string} values.password
 * @param {function} [errorCallback]
 * @returns {Promise} request
 */
const login = ({ email, password }, errorCallback) => {
  const { models: { requestTokens: { signin: signinToken } } } = getState()

  actions.models.ui.showRequestLoader()

  const body = {
    username: email.trim().toLowerCase(),
    password,
    token: signinToken,
  }

  return loginGql(body, errorCallback)
    .then((data) => {
      const {
        me, subscription, closedPopups, subscriptionProgress, token,
        me: { id, email, mixpanelClientUid },
      } = data

      actions.models.requestTokens.update({ [constants.requestTokens.signin]: token })

      setUserContext({ userId: id, email, mixpanelId: mixpanelClientUid })

      // Update store only after all requests
      const [ awaitFor, awaitForResolve, awaitForReject ] = deferredPromise()

      awaitFor.then(() => {
        actions.models.me.set(me)
        actions.models.ui.setClosedPopups(closedPopups)
        actions.models.subscription.set(subscription)
        reducers.models.payment.update({ subscriptionProgress })
      })

      actions.models.user.fetchCommonData(awaitFor)
        .then(awaitForResolve, awaitForReject)

      return awaitFor
    }, (errorData) => handleRejectUpdateToken({
      ...errorData,
      tokenName: constants.requestTokens.signin,
    }))
    .finally(() => actions.models.ui.hideRequestLoader())
}

const registerGql = async ({
  email,
  gender,
  password,
  passwordStrength,
  token,
  products,
  freeTrial,
  receiveUpdates,
}) => {
  // ATTN we use destruction, because there can be other fields
  const input = {
    email,
    gender,
    password,
    passwordStrength,
    token,
    products,
    freeTrial,
    receiveUpdates,
  }

  try {
    const responseData = await request.post('graphql?name=registerGql', {
      body: {
        query: registerQuery,
        variables: {
          input,
        },
      },
    })

    await checkErrors({ responseData: responseData.register })

    return getUserData(responseData.register)
  }
  catch (error) {
    const token = error.token || object.get(error, 'data.register.token')

    await callErrorProcessing({
      ...error,
      token,
      title: 'registerGql',
      input: {
        email: input.email,
        token,
      },
    })
  }
}

/**
 * Register new user
 *
 * @param {Object} values
 * @param {String} values.name
 * @param {String} values.email
 * @param {String} values.gender
 * @param {String} values.password
 * @param {String} values.passwordStrength
 * @param {String} values.receiveUpdates
 * @param {String} [values.token]
 * @returns {Promise} request
 */
const register = (values) => {
  const { models: { requestTokens: { registration: registrationToken } } } = getState()

  actions.models.ui.showRequestLoader()

  const products            = actions.view.pickPage.getSelectedItemsIdsFromStorage()
  const isFromOrgFreeTrial  = getIsFromFreeTrialLanding()

  const body = {
    ...values,
    products,
    token: registrationToken,
    freeTrial: isFromOrgFreeTrial,
  }

  return registerGql(body)
    .then((data) => {
      const {
        me, subscription, closedPopups, token,
        me: { id, email, mixpanelClientUid },
      } = data

      actions.models.requestTokens.update({ [constants.requestTokens.registration]: token })

      trackRegisterEvents()
      setUserContext({ userId: id, email, mixpanelId: mixpanelClientUid })

      // remove cookie from /ccpa page
      actions.misc.cookies.removeItem(constants.cookieNames.ccpa)

      // Update store only after all requests
      const [ awaitFor, awaitForResolve, awaitForReject ] = deferredPromise()

      awaitFor.then(() => {
        actions.models.me.set(me)
        actions.models.ui.setClosedPopups(closedPopups)
        actions.models.subscription.set(subscription)
        actions.models.me.saveNetflixRecommendationsLikes()
      })

      Promise.all([
        actions.models.user.fetchCommonData(awaitFor),
      ])
        .then(awaitForResolve, awaitForReject)

      return awaitFor
    }, (errorData) => handleRejectUpdateToken({
      ...errorData,
      tokenName: constants.requestTokens.registration,
    }))
}

const trackRegisterFail = (error) => {
  const { code, message } = error
  let type

  if (code && message) {
    type = `[${code}]: ${message}`
  }
  else if (code) {
    type = `[${code}]`
  }
  else if (message) {
    type = message
  }

  mixpanel.track(constants.analyticsEvents.registerFailed, {
    type,
  })
}

const getUserData = (data) => {
  const { realEmail, result, user, token } = data
  const { paymentProvider, me, closedPopups, subscription, subscriptionProgress } = user || {}

  return {
    me: {
      paymentProvider,
      ...me,
    },
    subscriptionProgress,
    subscription: modifySubscription(subscription || { status: {} }),
    closedPopups: modifyClosedPopups(isNotEmpty(closedPopups) ? closedPopups : []),
    realEmail,
    token,
    authenticationValid: result,
  }
}

const facebookGql = async (input) => {
  try {
    const responseData = await request.post('graphql?name=facebookSignin', {
      body: {
        query: facebookSigninQuery,
        variables: {
          input,
        },
      },
    })

    await checkErrors({ responseData: responseData.facebookSignin })

    return getUserData(responseData.facebookSignin)
  }
  catch (error) {
    const token = error.token || object.get(error, 'data.facebookSignin.token')

    await callErrorProcessing({
      ...error,
      token,
      title: 'facebookGql',
      input,
    })
  }
}

/**
 * Authorize with Facebook
 *
 * @param {Object} values
 * @param {String} values.tokenName
 * @param {String} [values.email]
 * @param {String} [values.password]
 * @returns {Promise} request
 */
const facebookAuth = (values) => {
  const { email, tokenName, password } = values
  const {
    models: {
      facebook: { auth },
      me: { selectedGender },
      requestTokens,
    },
  } = getState()

  if (!auth) {
    actions.models.modals.open(constants.modals.SomethingWentWrong, {
      endpoint: 'facebookAuth',
      error: 'no auth in facebook model',
    })

    sentry.captureException(`no auth in facebook model`, {
      extra: {
        auth,
        email,
      },
    })

    return Promise.reject()
  }

  const { accessToken, expiresIn, signedRequest, userID } = auth

  if (!accessToken || !signedRequest || !userID) {
    sentry.captureException('FacebookAuth with empty tokens', {
      extra: {
        auth,
      },
    })

    actions.models.modals.open(constants.modals.SomethingWentWrong, {
      endpoint: 'facebookAuth',
      error: 'no signedRequest || accessToken || userID',
      signedRequest,
      accessToken,
      userID,
    })

    return Promise.reject()
  }

  const products            = actions.view.pickPage.getSelectedItemsIdsFromStorage()
  const isFromOrgFreeTrial  = getIsFromFreeTrialLanding()

  actions.models.ui.showRequestLoader()

  const body = {
    email,
    password,
    gender: selectedGender,
    products,
    token: requestTokens[tokenName],
    accessToken, // data from FB button authResponse
    expiresIn, // data from FB button authResponse
    signedRequest, // data from FB button authResponse
    userId: userID, // data from FB button authResponse
    freeTrial: isFromOrgFreeTrial,
  }

  const isFacebookEmailRegistrationEnabled = getFeatureToggle(constants.features.facebookEmailRegistration)

  return facebookGql(body)
    .then(
      ({
        realEmail,
        authenticationValid,
        me,
        subscription,
        closedPopups,
        subscriptionProgress,
        token,
      }) => {
        actions.models.requestTokens.update({ [tokenName]: token })
        // Facebook email authentication https://scentbird.atlassian.net/browse/SCNT-2988
        // !password means that this action called not from FacebookRegisterModal
        if (!realEmail && !password && isFacebookEmailRegistrationEnabled) {
          // TODO remove logic for saving data in session storage when new register pages will be done
          actions.misc.cookies.setSessionItem(constants.cookieNames.facebookEmailRegistration, true)
          actions.models.router.push(links.facebookEmailRegistration)

          // break promise chain and pass "break" to prevent showing modal in components/modals/FacebookRegisterModal
          return Promise.reject('break')
        }

        // RegisterModal logic
        if (!authenticationValid) {
          return Promise.reject()
        }

        const { id, email, mixpanelClientUid } = me

        setUserContext({ userId: id, email, mixpanelId: mixpanelClientUid })

        // Update store only after all requests
        const [ awaitFor, awaitForResolve, awaitForReject ] = deferredPromise()

        awaitFor.then(() => {
          actions.models.me.set(me)
          actions.models.ui.setClosedPopups(closedPopups)
          actions.models.subscription.set(subscription)
          reducers.models.payment.update({ subscriptionProgress })
          actions.models.me.saveNetflixRecommendationsLikes()
        })

        Promise.all([
          actions.models.user.fetchCommonData(awaitFor),
        ])
          .then(awaitForResolve, awaitForReject)

        return awaitFor
      }, (errorData) => handleRejectUpdateToken({
        ...errorData,
        tokenName,
      })
    )
    .then(() => bindCouponToUser())
    .finally(() => actions.models.ui.hideRequestLoader())
}

const googleAuthGql = async (input) => {
  try {
    const responseData = await request.post('graphql?name=googleSignin', {
      body: {
        query: googleSigninQuery,
        variables: {
          input,
        },
      },
    })

    await checkErrors({ responseData: responseData.googleSignin })

    return getUserData(responseData.googleSignin)
  }
  catch (error) {
    const token = error.token || object.get(error, 'data.googleSignin.token')

    await callErrorProcessing({
      ...error,
      token,
      title: 'googleAuthGql',
      input,
    })
  }
}

/**
 * Authorize with Google
 *
 * @param {Object} values
 * @param {string} values.access_token
 * @param {string} values.id_token
 * @param {number} values.expires_in
 * @returns {Promise} request
 */
const googleAuth = (values) => {
  const { models: { me: { selectedGender }, requestTokens: { googleSignin: googleSigninToken } } } = getState()
  const { access_token: accessToken, id_token: idToken } = values

  const products            = actions.view.pickPage.getSelectedItemsIdsFromStorage()
  const isFromOrgFreeTrial  = getIsFromFreeTrialLanding()

  actions.models.ui.showRequestLoader()

  const body = {
    token: googleSigninToken,
    accessToken,
    idToken,
    gender: selectedGender,
    products,
    freeTrial: isFromOrgFreeTrial,
  }

  return googleAuthGql(body)
    .then(
      ({
        me,
        subscription,
        closedPopups,
        subscriptionProgress,
        token,
      }) => {
        const { id, email, mixpanelClientUid } = me

        actions.models.requestTokens.update({
          [constants.requestTokens.googleSignin]: token,
        })
        setUserContext({ userId: id, email, mixpanelId: mixpanelClientUid })

        // Update store only after all requests
        const [ awaitFor, awaitForResolve, awaitForReject ] = deferredPromise()

        awaitFor.then(() => {
          actions.models.me.set(me)
          actions.models.ui.setClosedPopups(closedPopups)
          actions.models.subscription.set(subscription)
          reducers.models.payment.update({ subscriptionProgress })
          actions.models.me.saveNetflixRecommendationsLikes()
        })

        actions.models.user.fetchCommonData(awaitFor)
          .then(awaitForResolve, awaitForReject)

        return awaitFor
      },
      (errorData) => handleRejectUpdateToken({
        ...errorData,
        tokenName: constants.requestTokens.googleSignin,
      })
    )
    .then(() => bindCouponToUser())
    .finally(() => actions.models.ui.hideRequestLoader())
}

/**
 *  * @param values:
 * {
        authorization: {
          state: string
          code: string
          id_token: string
        },
        // comes on first authorization only!
        user?: {
          email: string
          name: {
            firstName: string
            lastName: string
          }
        }
      }
 */
const appleAuth = (values) => {
  const { models: { requestTokens: { appleSignin: token } } } = getState()
  const { user, authorization: { code, state, id_token: idToken } } = values

  if (user) {
    localStorage.setItem('appleSignInUser', user)
  }

  const { email, name: { firstName, lastName } = {} } = user || localStorage.getItem('appleSignInUser') || {}

  const input = {
    code,
    state,
    idToken,
    firstName,
    lastName,
    email,
    token,
    mixpanelClientId: getCookieValue('_mxpnl_id', true),
  }

  actions.models.ui.showRequestLoader()

  return request.gql('appleSignin', {
    body: {
      query: appleSigninQuery,
      variables: { input },
    },
    withNestedErrors: true,
  })
    .then((data) => getUserData(data.appleSignin))
    .then(
      ({ me, subscription, closedPopups, subscriptionProgress, token }) => {
        const { id, email, mixpanelClientUid } = me

        localStorage.removeItem('appleSignInUser')

        actions.models.requestTokens.update({
          [constants.requestTokens.appleSignin]: token,
        })
        setUserContext({ userId: id, email, mixpanelId: mixpanelClientUid })

        // Update store only after all requests
        const [ awaitFor, awaitForResolve, awaitForReject ] = deferredPromise()

        awaitFor.then(() => {
          actions.models.me.set(me)
          actions.models.ui.setClosedPopups(closedPopups)
          actions.models.subscription.set(subscription)
          reducers.models.payment.update({ subscriptionProgress })
          actions.models.me.saveNetflixRecommendationsLikes()
        })

        actions.models.user.fetchCommonData(awaitFor)
          .then(awaitForResolve, awaitForReject)

        return awaitFor
      },
      (errorData) => handleRejectUpdateToken({
        ...errorData,
        tokenName: constants.requestTokens.appleSignin,
      })
    )
    .then(() => bindCouponToUser())
    .finally(() => actions.models.ui.hideRequestLoader())
}

/**
 * Request password restore link to passed email
 * [Security] This method always return status 200. Check SCNT-5134
 *
 * @param {String} email
 * @param {Object} options
 * @param {Boolean} options.withErrorModal
 * @returns {Promise} request
 */
const remindPassword = (email, options = {}) =>
  request.post('rest/user/password/restore', {
    body: {
      email: email.trim().toLowerCase(),
    },
    ...options,
  })

/**
 * Reset password action
 *
 * @param {String} body.token
 * @param {String} body.newPassword
 * @param {String} body.passwordStrength
 * @returns {Promise} request
 */
const resetPassword = (body) =>
  request.post('rest/user/password/restore/finish', {
    body,
    withErrorModal: false,
  })

/**
 * Check password reset token
 *
 * @param {String} token
 * @returns {Promise} request
 */
const checkResetPasswordToken = (token) =>
  request.post('rest/user/password/restore/validate', {
    body: { token },
    withErrorModal: false,
  })

/**
 * Get user email and gender who registered from lead paage
 *
 * @returns {Promise} request
 */
const getLeadInfo = () =>
  request.get('rest/lead/info')
    .then((result) => {
      actions.misc.cookies.setItem('leadInfo', result)
      return result
    })

const resolveAllPostponedItems = ({ isNotificationVisible, skipLimitedItems } = {}) =>
  Promise.all([
    actions.models.fragrance.resolvePostponedStockNotificationSubscription(),
    actions.models.queue.resolveAllPostponedItems({ isNotificationVisible }),
    !skipLimitedItems && actions.models.queue.resolveAllPostponedLimitedItems(),
    actions.models.cart.resolvePostponedItem(),
  ])

const setAuthEmail = (email) => {
  reducers.models.user.setAuthEmail(email)
}

const setInvalidEmailStatus = (valid) => {
  reducers.models.user.setInvalidEmailStatus(valid)
}


export default {
  login,
  register,
  appleAuth,
  googleAuth,
  getLeadInfo,
  setAuthEmail,
  facebookAuth,
  resetPassword,
  setUserContext,
  remindPassword,
  fetchCommonData,
  bindCouponToUser,
  handleMixpanelId,
  trackRegisterFail,
  checkExistByEmail,
  setNewrelicContext,
  setInvalidEmailStatus,
  checkResetPasswordToken,
  resolveAllPostponedItems,
}
